import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { buildCalendarEventFeedbackFormValues } from 'lib/calendar';
import {
  ContentLabel,
  ContentValue,
  DisplayFormattedDate,
  EmptyIndicator,
  User
} from 'components/common';
import { Box, BoxBackLink } from 'components/common/box';
import { Form } from 'components/common/form';
import { CalendarEventFeedbackSubmissionQuestion } from '.';

import type { GetCalendarEventFeedbackSubmissionByIdRes } from 'shared-types/responses';

interface Props {
  submissionId: number;
}

/**
 * Read-only view of one response, for owners and superadmins. Shows the same
 * form the participant filled in, disabled and populated with their answers
 */
const CalendarEventFeedbackResponse = ({ submissionId }: Props) => {
  const {
    messages,
    pageState: { calendarEvent }
  } = useReduxState();

  const { data, loading } =
    useApiData<GetCalendarEventFeedbackSubmissionByIdRes>({
      onlyIf: Boolean(submissionId),
      url: `/admin/calendar-events/${calendarEvent.id}/feedback-form/submissions/${submissionId}`
    });

  return (
    <div className="response">
      <Box
        loading={loading}
        title={
          <BoxBackLink simpleReturn={true} text={messages.commonFeedback} />
        }
      >
        {data ? (
          <div className="summary">
            <User
              anonymous={!data.user}
              enableProfileLink={Boolean(data.user)}
              user={data.user || undefined}
            />

            <ContentLabel horizontal={true} label={messages.commonSubmitted}>
              <ContentValue style={{ fontWeight: 600 }}>
                <DisplayFormattedDate date={data.createdAt} />
              </ContentValue>
            </ContentLabel>

            <ContentLabel horizontal={true} label={messages.tasksAnswers}>
              <ContentValue style={{ fontWeight: 600 }}>
                {data.answers?.length}
              </ContentValue>
            </ContentLabel>
          </div>
        ) : null}
      </Box>

      {!loading && !data?.questions?.length ? (
        <Box style={{ marginTop: 25 }}>
          <EmptyIndicator
            icon="question-circle"
            title={messages.eventCalendarFeedbackQuestionsEmpty}
          />
        </Box>
      ) : data ? (
        <Box style={{ marginTop: 25 }} title={messages.commonQuestions}>
          <Form
            initialValues={buildCalendarEventFeedbackFormValues({
              answers: data.answers,
              questions: data.questions
            })}
          >
            <div className="questions">
              {data.questions.map((question) => (
                <CalendarEventFeedbackSubmissionQuestion
                  disabled={true}
                  key={question.id}
                  question={question}
                />
              ))}
            </div>
          </Form>
        </Box>
      ) : null}

      <style jsx>{`
        .questions {
          display: flex;
          flex-direction: column;
          gap: 30px;
        }
      `}</style>
    </div>
  );
};

export default CalendarEventFeedbackResponse;
